import { motion } from "framer-motion";
import { MaskedHeading, Reveal } from "@/components/primitives";
import { Cta } from "@/components/Cta";
import WorkflowDiagram from "@/components/WorkflowDiagram";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
    return (
        <section className="relative overflow-hidden pt-36 pb-20 md:pt-44 md:pb-28">
            {/* subtle background grid */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 -z-10 opacity-[0.35] dark:opacity-20"
                style={{
                    backgroundImage:
                        "linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px)",
                    backgroundSize: "64px 64px",
                    maskImage:
                        "radial-gradient(ellipse 80% 60% at 50% 0%, black, transparent 75%)",
                }}
            />

            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <div className="grid items-center gap-14 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, ease: EASE }}
                            className="mb-7 inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-navy" />
                            AI Automation Partner · Shipping &amp; Logistics
                        </motion.div>

                        <MaskedHeading
                            as="h1"
                            lines={["AI Automation for", "Shipping Operations"]}
                            delay={0.1}
                            className="text-balance text-5xl font-extrabold leading-[1.05] tracking-tight md:text-6xl lg:text-7xl"
                        />

                        <Reveal delay={0.5} className="mt-7 max-w-xl">
                            <p className="text-lg leading-relaxed text-muted-foreground">
                                Helping shipping and logistics companies
                                eliminate repetitive operational work through
                                practical AI automation.
                            </p>
                        </Reveal>

                        <Reveal delay={0.65}>
                            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                                <Cta
                                    to="/contact"
                                    size="lg"
                                    variant="primary"
                                    testId="hero-primary-cta"
                                    withArrow={false}
                                >
                                    Book Free AI Automation Assessment
                                </Cta>
                                <Cta
                                    to="/contact"
                                    size="lg"
                                    variant="secondary"
                                    testId="hero-secondary-cta"
                                >
                                    Talk to an AI Specialist
                                </Cta>
                            </div>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-5">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.98 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 1, ease: EASE, delay: 0.4 }}
                            className="rounded-3xl border border-border bg-card/60 p-6 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)] backdrop-blur-sm"
                        >
                            <div className="mb-4 flex items-center justify-between">
                                <span className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
                                    Operational Flow
                                </span>
                                <span className="flex items-center gap-1.5 font-mono text-xs text-navy">
                                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-navy" />
                                    automated
                                </span>
                            </div>
                            <WorkflowDiagram />
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
}
